import { SidebarLink } from "@/components/Navigation";
import SidebarHeader from "@/components/Navigation/SidebarHeader";
import useNavigationMenu from "@/navigation/useNavigationMenu";
import { useUser } from "@/requests/hooks/user";
import { doesUserHaveScope } from "@/utils/doesUserHaveScope";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import {
  faArrowLeftFromLine,
  faArrowRightFromLine,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { tv } from "tailwind-variants";

const TOP_NAV_HEIGHT = 189;
const SUPERADMIN_HEIGHT = 74;

const styles = tv({
  slots: {
    base: "group relative z-40 hidden flex-col justify-between bg-secondary text-white transition-width sm:flex",
    itemsParent:
      "top-0 z-0 flex w-350 flex-col justify-between bg-secondary pt-50 text-white transition-width",
    items: "flex w-full flex-grow flex-col",
    toggle: "flex cursor-pointer justify-start p-30",
  },
  variants: {
    isExpanded: {
      true: {
        base: "w-350",
        itemsParent: "w-350",
      },
      false: {
        base: "absolute bottom-0 left-0 top-0 w-80 hover:w-350",
        itemsParent: "w-80 group-hover:w-350",
      },
    },
    fixedNavbar: {
      true: {
        itemsParent: "fixed h-screen pt-100",
      },
    },
    // stub for use in styles call
    isOnboarding: {
      true: {
        itemsParent: "pt-0",
      },
    },
  },
  compoundVariants: [
    {
      isExpanded: true,
      fixedNavbar: true,
      className: {
        base: "min-w-350",
      },
    },
    {
      isOnboarding: true,
      fixedNavbar: true,
      className: {
        itemsParent: "pt-50",
      },
    },
  ],
});

interface IProps {
  isExpanded: boolean;
  toggle: () => void;
}

const Sidebar = ({ isExpanded, toggle }: IProps) => {
  const { pathname } = useLocation();
  const navigation = useNavigationMenu();
  const [offset, setOffset] = useState<number>(0);
  const { data: user } = useUser();

  const { onboardingStatus } = useOnboardingStatus();

  const {
    base,
    itemsParent: itemsParentContainer,
    items: itemsContainer,
    toggle: toggleContainer,
  } = styles({
    isExpanded,
    fixedNavbar: offset >= 108,
    isOnboarding: onboardingStatus.isOnboarding,
  });

  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const getComputedHeight = () => {
    if (offset >= 108) return `100vh`;

    const scrollOffset =
      TOP_NAV_HEIGHT -
      offset +
      (doesUserHaveScope("JITA", user?.scopes) ? SUPERADMIN_HEIGHT : 0);
    return `calc(100vh - ${scrollOffset}px)`;
  };

  const activeMenuItem = navigation.find(({ url }) => pathname.startsWith(url));

  const renderItems = () => {
    return activeMenuItem?.sidebar
      .filter(({ hidden }) => !!!hidden?.())
      .map(({ icon, url, title, external, newTab }, index) => {
        const fullUrl = external ? url : `${activeMenuItem.url}${url}`;

        const screenSidebarItem = fullUrl.split("/")[2];
        const activeSidebarItem = pathname.split("/")[2];

        return (
          <SidebarLink
            key={index}
            icon={icon}
            title={title}
            url={fullUrl}
            isExternal={external}
            isNewTab={newTab}
            isActive={screenSidebarItem === activeSidebarItem}
            isSidebarExpanded={isExpanded}
          />
        );
      });
  };

  const renderToggleIcon = () => (
    <FontAwesomeIcon
      fixedWidth
      icon={isExpanded ? faArrowLeftFromLine : faArrowRightFromLine}
    />
  );

  if (!activeMenuItem) return null;

  return (
    <div className={base()} role="menu">
      <div
        className={itemsParentContainer()}
        style={{
          height: getComputedHeight(),
        }}
        data-testid="sidebar-items-parent-container">
        <div className={itemsContainer()}>
          <SidebarHeader isSidebarExpanded={isExpanded} />
          {renderItems()}
        </div>
        <button
          className={toggleContainer()}
          onClick={toggle}
          data-testid="sidebar-toggle-button">
          {renderToggleIcon()}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
